import { plain } from '../../lib/labels';
import { Alert, Descriptions, Empty, Space, Table, Tag, Typography, type TableColumnsType } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { formatRupees } from '../../lib/money';
import { UserName } from '../../lib/listenerName';
import { formatWhen } from './listenerStats';

/** The listener row as `GET /admin/listeners` returns it (the application + KYC). */
export interface ListenerProfile {
  id: string;
  userId: string;
  displayName: string;
  bio: string;
  tier: string;
  status: string;
  isSuspended: boolean;
  suspendedReason?: string | null;
  voiceVerifiedAt?: string | null;
  verifiedProfessional: boolean;
  perMinuteRatePaise: string;
  languages: string[];
  expertiseTags: string[];
  rejectionReason: string | null;
  rejectedAt?: string | null;
  createdAt: string;
  user: { handle: string; phoneLast4: string };
  kycDocuments: Array<{
    id: string;
    type: string;
    status: 'uploaded' | 'verified' | 'rejected';
    last4?: string | null;
    rejectionReason?: string | null;
    createdAt: string;
  }>;
}

/** One listener's profile row — found by id through the same list endpoint the Listeners page uses. */
export function useListenerProfile(id: string) {
  return useQuery({
    queryKey: ['admin', 'listeners', 'profile', id],
    queryFn: async () => {
      const now = new Date();
      const res = await api.get<{ items: ListenerProfile[] }>('/admin/listeners', {
        params: {
          q: id,
          page: 1,
          pageSize: 5,
          from: new Date(now.getTime() - 86_400_000).toISOString(),
          to: now.toISOString(),
        },
      });
      return res.data.items.find((l) => l.id === id) ?? null;
    },
    enabled: id !== '',
  });
}

const KYC_COLOR = { uploaded: 'processing', verified: 'success', rejected: 'error' } as const;

/** What the listener applied with, the decision, and their KYC documents. */
export function ApplicationTab({ profile, loading }: { profile: ListenerProfile | null | undefined; loading: boolean }) {
  if (loading) return <Typography.Text type="secondary">Loading…</Typography.Text>;
  if (!profile) return <Empty description="Application details are not available." />;
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Descriptions size="small" bordered column={{ xs: 1, md: 2 }}>
        <Descriptions.Item label="Application">
          <Space size={4} wrap>
            <Tag color={profile.status === 'approved' ? 'success' : profile.status === 'rejected' ? 'error' : 'processing'}>
              {plain(profile.status)}
            </Tag>
            {profile.isSuspended && <Tag color="error">suspended</Tag>}
            {profile.verifiedProfessional && <Tag color="gold">verified professional</Tag>}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Applied on">{formatWhen(profile.createdAt)}</Descriptions.Item>
        <Descriptions.Item label="Account">
          <UserName handle={profile.user.handle} userId={profile.userId} /> · ••• {profile.user.phoneLast4}
        </Descriptions.Item>
        <Descriptions.Item label="Tier">{profile.tier}</Descriptions.Item>
        <Descriptions.Item label="Video rate">{formatRupees(profile.perMinuteRatePaise)} / min</Descriptions.Item>
        <Descriptions.Item label="Languages">{profile.languages.join(', ') || '—'}</Descriptions.Item>
        <Descriptions.Item label="Expertise" span={2}>
          {profile.expertiseTags.length ? profile.expertiseTags.map((t) => <Tag key={t}>{plain(t)}</Tag>) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Bio" span={2}>
          <span style={{ whiteSpace: 'pre-line' }}>{profile.bio || '—'}</span>
        </Descriptions.Item>
        {profile.status === 'rejected' && (
          <Descriptions.Item label="Rejected" span={2}>
            {profile.rejectionReason || 'No reason recorded'}
            {profile.rejectedAt ? ` · ${formatWhen(profile.rejectedAt)}` : ''}
          </Descriptions.Item>
        )}
        {profile.isSuspended && profile.suspendedReason && (
          <Descriptions.Item label="Suspension reason" span={2}>
            {profile.suspendedReason}
          </Descriptions.Item>
        )}
      </Descriptions>
      <Table
        size="small"
        rowKey="id"
        title={() => <strong>KYC documents</strong>}
        dataSource={profile.kycDocuments}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No KYC documents uploaded' }}
        columns={[
          { title: 'Type', dataIndex: 'type', render: (t: string) => <Tag>{t.toUpperCase()}</Tag> },
          {
            title: 'Status',
            dataIndex: 'status',
            render: (s: keyof typeof KYC_COLOR) => <Tag color={KYC_COLOR[s]}>{plain(s)}</Tag>,
          },
          { title: 'Last 4', dataIndex: 'last4', render: (v: string | null) => v ?? '—' },
          { title: 'Uploaded', dataIndex: 'createdAt', render: (v: string) => formatWhen(v) },
          { title: 'Rejection reason', dataIndex: 'rejectionReason', render: (v: string | null) => v ?? '—' },
        ]}
      />
    </Space>
  );
}

interface ReferralRow {
  id: string;
  status: string;
  source: string | null;
  referrerId: string;
  referrerHandle: string;
  refereeId: string;
  refereeHandle: string;
  /** The users' own Display Names — absent on older APIs. */
  referrerDisplayName?: string | null;
  refereeDisplayName?: string | null;
  createdAt: string;
  rewardedAt: string | null;
  referrerReward: { kind: string; coins: number; paise: string };
}

/** Referrals this listener's account made, and the one that brought them in. */
export function ReferralTab({ userId }: { userId: string | undefined }) {
  const referrals = useQuery({
    queryKey: ['admin', 'referral', 'by-user', userId],
    queryFn: async () =>
      (await api.get<{ items: ReferralRow[] }>('/admin/referral/referrals', { params: { userId, limit: 100 } })).data,
    enabled: !!userId,
  });
  const columns: TableColumnsType<ReferralRow> = [
    {
      title: 'Role',
      render: (_: unknown, r) =>
        r.referrerId === userId ? <Tag color="blue">Referred someone</Tag> : <Tag color="purple">Was referred</Tag>,
    },
    {
      title: 'Other account',
      render: (_: unknown, r) =>
        r.referrerId === userId ? (
          <UserName handle={r.refereeHandle} displayName={r.refereeDisplayName} userId={r.refereeId} />
        ) : (
          <UserName handle={r.referrerHandle} displayName={r.referrerDisplayName} userId={r.referrerId} />
        ),
    },
    { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag>{s === 'pending' ? 'Waiting to qualify' : plain(s)}</Tag> },
    {
      title: 'Referrer reward',
      render: (_: unknown, r) =>
        r.referrerReward.kind === 'none'
          ? '—'
          : r.referrerReward.coins > 0
            ? `${r.referrerReward.coins} coins`
            : formatRupees(r.referrerReward.paise),
    },
    { title: 'Created', dataIndex: 'createdAt', render: (v: string) => formatWhen(v) },
    { title: 'Rewarded', dataIndex: 'rewardedAt', render: (v: string | null) => (v ? formatWhen(v) : '—') },
  ];
  if (referrals.isError) return <Alert type="error" showIcon message="Could not load referrals" />;
  return (
    <Table
      size="small"
      rowKey="id"
      loading={referrals.isLoading || !userId}
      dataSource={referrals.data?.items ?? []}
      columns={columns}
      pagination={{ pageSize: 25 }}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'This listener has no referrals' }}
    />
  );
}

interface AuditRow {
  id: string;
  action: string;
  actorId: string | null;
  actorType: string;
  actorName?: string | null;
  actorEmail?: string | null;
  afterDiff?: unknown;
  createdAt: string;
}

/** What each admin action on a listener means, in plain words. */
const STATUS_ACTION_LABEL: Record<string, string> = {
  'listener.approve': 'Approved',
  'listener.reject': 'Rejected',
  'listener.suspend': 'Suspended',
  'listener.unsuspend': 'Suspension lifted',
  'listener.schedule_interview': 'Interview scheduled',
  'listener.set_rate': 'Rate changed',
  'listener.dummy': 'Dummy flag changed',
  'listener.hot.set': 'Hot mark changed',
  'listener.verify_professional': 'Professional verification changed',
};

/** Every admin action recorded against this listener (the audit log), newest first. */
export function StatusHistoryTab({ listenerId }: { listenerId: string }) {
  const history = useQuery({
    queryKey: ['admin', 'audit', 'listener', listenerId],
    queryFn: async () =>
      (
        await api.get<{ items: AuditRow[] }>('/admin/audit-log', {
          params: { targetType: 'Listener', targetId: listenerId, limit: 100 },
        })
      ).data,
  });
  if (history.isError) return <Alert type="error" showIcon message="Could not load the status history" />;
  return (
    <Table
      size="small"
      rowKey="id"
      loading={history.isLoading}
      dataSource={history.data?.items ?? []}
      pagination={{ pageSize: 25 }}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'No admin actions recorded for this listener' }}
      columns={[
        { title: 'When', dataIndex: 'createdAt', render: (v: string) => formatWhen(v) },
        {
          title: 'Change',
          dataIndex: 'action',
          render: (a: string) => (
            <Space size={6}>
              <strong>{STATUS_ACTION_LABEL[a] ?? a}</strong>
              {STATUS_ACTION_LABEL[a] && (
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {a}
                </Typography.Text>
              )}
            </Space>
          ),
        },
        {
          title: 'By',
          render: (_: unknown, r: AuditRow) =>
            r.actorName ?? r.actorEmail ?? (r.actorId ? `${r.actorType} ${r.actorId.slice(0, 8)}` : r.actorType),
        },
        {
          title: 'Details',
          render: (_: unknown, r: AuditRow) => {
            const d = r.afterDiff;
            const reason = d && typeof d === 'object' && 'reason' in d && typeof d.reason === 'string' ? d.reason : null;
            return reason ?? '—';
          },
        },
      ]}
    />
  );
}
